import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { ArrowLeftIcon, PencilIcon } from '@heroicons/react/24/outline';
import AiWritingAssistant from '../../components/Projects/AiWritingAssistant';
import AiFieldAssistant from '../../components/Projects/AiFieldAssistant';

const EditProject = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    stage: '',
    fundingNeeded: '',
    currency: '',
    country: '',
    businessModel: '',
    targetMarket: '',
    competitiveAdvantage: '',
    teamSize: '',
    expectedROI: '',
    timeline: '',
    contactInfo: ''
  });

  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);

  // Récupérer le projet à modifier
  // api (services/api.js) auto-extrait { success: true, data: {...} } → response.data = project
  const { isLoading: projectLoading } = useQuery(
    ['project', id],
    async () => {
      const response = await api.get(`/projects/${id}`);
      return response.data;
    },
    {
      enabled: !!id,
      onSuccess: (data) => {
        // Vérifier que l'utilisateur peut modifier ce projet
        if (data.authorId !== user?.id && user?.role !== 'admin') {
          toast.error('Vous ne pouvez modifier que vos propres projets');
          navigate('/projects');
          return;
        }

        // Pré-remplir le formulaire
        // Backend retourne: funding.amountNeeded, funding.currency, country (code)
        setFormData({
          title: data.title || '',
          description: data.description || '',
          category: data.category || '',
          stage: data.stage || '',
          fundingNeeded: data.funding?.amountNeeded || '',
          currency: data.funding?.currency || '',
          country: data.country || '',
          businessModel: data.businessModel || '',
          targetMarket: data.targetMarket || '',
          competitiveAdvantage: data.competitiveAdvantage || '',
          teamSize: data.teamSize || '',
          expectedROI: data.expectedROI || '',
          timeline: data.timeline || '',
          contactInfo: data.contactInfo || ''
        });
      },
      onError: (error) => {
        console.error('Erreur chargement projet:', error);
        toast.error('Erreur lors du chargement du projet');
        navigate('/projects');
      }
    }
  );

  // Récupérer les pays et devises
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [countriesRes, currenciesRes] = await Promise.all([
          axios.get('/api/countries'),
          axios.get('/api/currencies')
        ]);
        // Backend retourne { success: true, data: [...] } pour countries
        // et { success: true, currencies: [...] } pour currencies
        setCountries(Array.isArray(countriesRes.data?.data) ? countriesRes.data.data : []);
        setCurrencies(Array.isArray(currenciesRes.data?.currencies) ? currenciesRes.data.currencies : []);
      } catch (error) {
        console.error('Erreur chargement données:', error);
        toast.error('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Mutation pour modifier le projet (api inclut le token auth + auto-extrait)
  const updateProjectMutation = useMutation(
    async (data) => {
      const response = await api.put(`/projects/${id}`, data);
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success('Projet modifié avec succès !');
        queryClient.invalidateQueries(['project', id]);
        queryClient.invalidateQueries('projects');
        navigate(`/projects/${id}`);
      },
      onError: (error) => {
        console.error('Erreur modification projet:', error);
        toast.error(error.response?.data?.message || 'Erreur lors de la modification');
      }
    }
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation côté client
    if (!formData.title.trim() || !formData.description.trim() || !formData.category || 
        !formData.stage || !formData.fundingNeeded || !formData.currency || !formData.country) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    updateProjectMutation.mutate(formData);
  };

  const categories = [
    'Technologie',
    'Santé',
    'Éducation',
    'Environnement',
    'Finance',
    'E-commerce',
    'Transport',
    'Immobilier',
    'Agriculture',
    'Tourisme',
    'Autre'
  ];

  const stages = [
    'Idée',
    'Prototype',
    'MVP',
    'Développement',
    'Test',
    'Lancement',
    'Croissance',
    'Expansion'
  ];

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-text mb-4">Accès non autorisé</h2>
          <p className="text-gray-600 mb-6">Vous devez être connecté pour modifier un projet.</p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Se connecter
          </button>
        </div>
      </div>
    );
  }

  if (projectLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-800 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Retour
          </button>
          
          <div className="flex items-center">
            <PencilIcon className="h-8 w-8 text-primary-500 mr-3" />
            <div>
              <h1 className="text-3xl font-bold text-text">Modifier le projet</h1>
              <p className="text-gray-600 mt-1">Mettez à jour les informations de votre projet</p>
            </div>
          </div>
        </div>

        {/* Formulaire */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Informations de base */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titre du projet *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Titre de votre projet"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Décrivez votre projet en détail"
                  required
                />
                <AiWritingAssistant
                  formData={formData}
                  countryName={countries.find(c => c.code === formData.country)?.name}
                  onApply={updates => setFormData(prev => ({ ...prev, ...updates }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Catégorie *
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                >
                  <option value="">Sélectionner une catégorie</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Stade du projet *
                </label>
                <select
                  name="stage"
                  value={formData.stage}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                >
                  <option value="">Sélectionner un stade</option>
                  {stages.map(stage => (
                    <option key={stage} value={stage}>{stage}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Financement recherché *
                </label>
                <input
                  type="number"
                  name="fundingNeeded"
                  value={formData.fundingNeeded}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Montant en chiffres"
                  min="0"
                  step="0.01"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Devise *
                </label>
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                >
                  <option value="">Sélectionner une devise</option>
                  {currencies.map(currency => (
                    <option key={currency.code} value={currency.code}>
                      {currency.code} - {currency.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pays *
                </label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                >
                  <option value="">Sélectionner un pays</option>
                  {countries.map(country => (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Taille de l'équipe
                </label>
                <input
                  type="number"
                  name="teamSize"
                  value={formData.teamSize}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Nombre de personnes"
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ROI attendu (%)
                </label>
                <input
                  type="number"
                  name="expectedROI"
                  value={formData.expectedROI}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Pourcentage"
                  min="0"
                  step="0.1"
                />
              </div>
            </div>

            {/* Informations détaillées */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-text border-b border-gray-200 pb-2">
                Informations détaillées
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Modèle économique
                </label>
                <textarea
                  name="businessModel"
                  value={formData.businessModel}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Décrivez votre modèle économique"
                />
                  <AiFieldAssistant
                    field="businessModel"
                    label="Modèle économique"
                    formData={formData}
                    countryName={countries.find(c => c.code === formData.country)?.name}
                    onApply={text => setFormData(prev => ({ ...prev, businessModel: text }))}
                  />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Marché cible
                </label>
                <textarea
                  name="targetMarket"
                  value={formData.targetMarket}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Décrivez votre marché cible"
                />
                  <AiFieldAssistant
                    field="targetMarket"
                    label="Marché cible"
                    formData={formData}
                    countryName={countries.find(c => c.code === formData.country)?.name}
                    onApply={text => setFormData(prev => ({ ...prev, targetMarket: text }))}
                  />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Avantage concurrentiel
                </label>
                <textarea
                  name="competitiveAdvantage"
                  value={formData.competitiveAdvantage}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Quel est votre avantage concurrentiel ?"
                />
                  <AiFieldAssistant
                    field="competitiveAdvantage"
                    label="Avantage concurrentiel"
                    formData={formData}
                    countryName={countries.find(c => c.code === formData.country)?.name}
                    onApply={text => setFormData(prev => ({ ...prev, competitiveAdvantage: text }))}
                  />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Timeline
                </label>
                <textarea
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Décrivez votre timeline de développement"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Informations de contact
                </label>
                <textarea
                  name="contactInfo"
                  value={formData.contactInfo}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Email, téléphone, etc."
                />
                <p className="mt-1 text-xs text-gray-500">Visible par tous les visiteurs de la Bourse, même sans compte. N'écrivez que ce que vous acceptez de rendre public.</p>
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={updateProjectMutation.isLoading}
                className="px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {updateProjectMutation.isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Modification...
                  </>
                ) : (
                  <>
                    <PencilIcon className="h-4 w-4 mr-2" />
                    Modifier le projet
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditProject;